<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <template v-for="route in visibleSections" :key="route.name">
        <VaCollapse>
          <template #header="{ value: isCollapsed }">
            <VaSidebarItem
              :to="route.children ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="sectionTextColor(route)"
              :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
              role="button"
              hover-opacity="0.10"
              @mouseenter="prefetchRoute(route)"
              @focusin="prefetchRoute(route)"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-4">
                <VaIcon
                  v-if="route.meta.icon"
                  aria-hidden="true"
                  :name="route.meta.icon"
                  size="20px"
                  :color="iconColor(route)"
                />
                <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold gap-2">
                  <span class="flex items-center gap-2 min-w-0">
                    <span class="truncate">{{ t(route.displayName) }}</span>
                    <SidebarNavBadge v-bind="navBadgeProps(route)" />
                  </span>
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>
          <template #body>
            <div v-for="childRoute in route.children" :key="childRoute.name">
              <VaSidebarItem
                v-if="isChildRouteVisible(childRoute)"
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="childTextColor(childRoute)"
                :aria-label="`Visit ${t(route.displayName)}`"
                hover-opacity="0.10"
                @mouseenter="prefetchRoute(childRoute)"
                @focusin="prefetchRoute(childRoute)"
              >
                <VaSidebarItemContent class="py-3 pr-2 pl-11">
                  <VaSidebarItemTitle class="leading-5 font-semibold flex items-center gap-2">
                    <span>{{ t(childRoute.displayName) }}</span>
                    <SidebarNavBadge v-bind="navBadgeProps(childRoute)" />
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </template>
        </VaCollapse>
      </template>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useUserStore } from '../../store/user'
import { useConfigurationStore } from '../../store/configurations'
import { isInventoryConfigEnabled } from '../../modules/inventory/utils/transfersConfig'
import { prefetchNamedRoute } from '../../utils/prefetchNamedRoute'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import SidebarNavBadge from './SidebarNavBadge.vue'
import { useSidebarNavBadges } from './useSidebarNavBadges'
import { isSidebarNavItemActive, isSidebarSectionActive } from './sidebarActiveRoute'
import { userMeetsNavGate } from '../../utils/routeAccess'

export default defineComponent({
  name: 'Sidebar',
  components: { SidebarNavBadge },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const userStore = useUserStore()
    const configStore = useConfigurationStore()

    const value = ref<boolean[]>([])

    const prefetchRoute = (navRoute: INavigationRoute) => {
      // Accordion parents have children and no `:to`; only warm leaf destinations.
      if (navRoute.children?.length) {
        return
      }
      prefetchNamedRoute(router, navRoute.name)
    }

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const hasPermission = (param: string | string[] | undefined, isParent: boolean = false) =>
      checkPermission(param, isParent)

    const checkPermission = (param: string | string[] | undefined, isParent: boolean = false) =>
      userMeetsNavGate(
        param,
        isParent,
        (slug) => userStore.hasModule(slug),
        (action, subject) => userStore.hasPermission(action, subject),
      )

    const meetsInventoryConfigRequirement = (child: INavigationRoute): boolean => {
      if (!child.requiresInventoryConfig) {
        return true
      }

      return isInventoryConfigEnabled(configStore.inventoryConfigs, child.requiresInventoryConfig)
    }

    const isChildRouteVisible = (child: INavigationRoute): boolean =>
      Boolean(
        (!child.gate || hasPermission(child.gate)) && !child.hideInSidebar && meetsInventoryConfigRequirement(child),
      )

    const currentRouteContext = () => ({
      name: route.name,
      path: route.path,
      matchedNames: route.matched.map((record) => record.name),
    })

    const resolvedPathFor = (routeName: string): string | null => {
      const resolved = router.resolve({ name: routeName })
      return resolved.name === routeName ? resolved.path : null
    }

    const isActiveChildRoute = (child: INavigationRoute) =>
      isSidebarNavItemActive(child, currentRouteContext(), resolvedPathFor(child.name))

    const routeHasActiveChild = (section: INavigationRoute) =>
      isSidebarSectionActive(section, currentRouteContext(), resolvedPathFor)

    const visibleSections = computed(() =>
      navigationRoutes.routes.filter((section) => !section.gate || hasPermission(section.gate, true)),
    )

    const setActiveExpand = () => {
      value.value = visibleSections.value.map((section) => routeHasActiveChild(section))
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (section: INavigationRoute) => (routeHasActiveChild(section) ? 'primary' : 'secondary')
    const sectionTextColor = (section: INavigationRoute) => (routeHasActiveChild(section) ? 'primary' : 'textPrimary')
    const childTextColor = (child: INavigationRoute) => (isActiveChildRoute(child) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch([() => route.fullPath, visibleSections], setActiveExpand, { immediate: true })

    const { navBadgeProps } = useSidebarNavBadges(isChildRouteVisible)

    return {
      writableVisible,
      isChildRouteVisible,
      visibleSections,
      sidebarWidth,
      value,
      color,
      activeColor,
      routeHasActiveChild,
      isActiveChildRoute,
      navBadgeProps,
      prefetchRoute,
      t,
      iconColor,
      sectionTextColor,
      childTextColor,
      arrowDirection,
    }
  },
})
</script>
